import { GlobeIcon } from "lucide-react";
import { useState } from "react";

import type { ProbeSource } from "../../../state/probeSession";
import { safeExternalHref } from "../../../lib/safeExternalHref";
import { WebsiteToolIcon } from "../../chat/WebsiteToolIcon";
import { canFold, foldSources, sourceHost, sourceLabel } from "./ProbeSources.logic";

const chip =
  "inline-flex max-w-64 items-center gap-1.5 rounded-full border border-border/60 bg-background px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground";

/**
 * The pages the examiner read on the web, under the line that used them:
 * each page's title beside its site's favicon, opening the page in a new
 * tab, with the site in the tooltip. The best few show; the rest fold into
 * "+N more". The voice never reads an address out, so this is where the
 * person finds them.
 */
export function ProbeSources({ sources }: { readonly sources: ReadonlyArray<ProbeSource> }) {
  const [expanded, setExpanded] = useState(false);
  const { shown, hidden } = foldSources(sources, expanded);
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Sources the examiner checked">
      {shown.map((source) => {
        const href = safeExternalHref(source.url);
        const content = (
          <>
            <WebsiteToolIcon pageUrl={source.url} fallback={<GlobeIcon className="size-3.5 shrink-0" />} />
            <span className="truncate">{sourceLabel(source)}</span>
          </>
        );
        return (
          <li key={source.url} className="min-w-0">
            {href === null ? (
              <span className={chip}>{content}</span>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={`${sourceLabel(source)} — ${sourceHost(source.url)}`}
                className={chip}
              >
                {content}
              </a>
            )}
          </li>
        );
      })}
      {canFold(sources) && (
        <li>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className={chip}
          >
            {expanded ? "Show fewer" : `+${hidden} more`}
          </button>
        </li>
      )}
    </ul>
  );
}
